import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../../components/Navbar";
import { formatApiError } from "../../lib/api";
import { mk, photoUrl } from "../../lib/market";
import { EmailGate } from "../../components/market/EmailGate";
import { useMarketMe } from "./MarketHome";

function Chat({ id, onSent }) {
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const end = useRef();
  const seq = useRef(0);
  const load = useCallback(() => {
    const n = ++seq.current;
    return mk.get(`/market/threads/${id}`).then((r) => { if (n === seq.current) setT(r.data); }).catch(() => {});
  }, [id]);
  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [t?.messages?.length]);
  async function send(e) {
    e.preventDefault();
    try { await mk.post(`/market/threads/${id}/messages`, { text }); setText(""); await load(); onSent(); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  if (!t) return null;
  return (
    <div className="flex flex-col h-[70vh] bg-white border border-[var(--line)] rounded-3xl overflow-hidden" data-testid="market-chat">
      <Link to={`/market/item/${t.listing_id}`} className="flex items-center gap-3 p-4 border-b border-[var(--line)]"><img src={photoUrl(t.photo)} alt="" className="w-12 h-12 rounded-xl object-cover" /><span><b className="block">{t.title}</b><span className="text-xs text-[var(--muted)]">{t.role === "seller" ? `Buyer: ${t.buyer_name}` : "You're the buyer"}</span></span></Link>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {t.messages.map((m, i) => <div key={i} className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${m.from === t.role ? "ml-auto bg-[var(--ink)] text-white" : "bg-[var(--bg-warm)]"}`} data-testid={`chat-msg-${i}`}>{m.text}</div>)}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t border-[var(--line)]">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Write a message" className="input-luxe flex-1" data-testid="chat-input" />
        <button disabled={!text.trim()} className="btn-gold !px-4" data-testid="chat-send"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}

export default function MarketInbox() {
  const [me, reloadMe] = useMarketMe();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const active = params.get("t");
  const load = () => mk.get("/market/threads").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { if (me) load(); }, [me, active]);
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Navbar />
      <div className="container-wide px-5 md:px-12 pt-28 pb-20">
        <Link to="/market" className="text-sm text-[var(--muted)] flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Marketplace</Link>
        <h1 className="font-serif font-light text-4xl mb-8">Marketplace messages</h1>
        {me === null && <EmailGate onDone={reloadMe} />}
        {me && (
          <div className="grid md:grid-cols-[320px_1fr] gap-6" data-testid="market-inbox">
            <div className={`space-y-2 ${active ? "hidden md:block" : ""}`}>
              {!list.length && <p className="text-[var(--muted)]" data-testid="inbox-empty">No conversations yet.</p>}
              {list.map((t) => (
                <button key={t.thread_id} onClick={() => setParams({ t: t.thread_id })} className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left border transition-colors ${active === t.thread_id ? "border-[var(--gold)] bg-white" : "border-transparent hover:bg-white"}`} data-testid={`inbox-thread-${t.thread_id}`}>
                  <img src={photoUrl(t.photo)} alt="" className="w-12 h-12 rounded-xl object-cover" />
                  <span className="flex-1 min-w-0"><b className="block truncate text-sm">{t.title}</b><span className="block truncate text-xs text-[var(--muted)]">{t.role === "seller" ? "Selling · " : "Buying · "}{t.last?.text}</span></span>
                  {t.unread && <span className="w-2.5 h-2.5 rounded-full bg-[var(--gold)]" data-testid={`inbox-unread-${t.thread_id}`} />}
                </button>
              ))}
            </div>
            {active ? <div><button onClick={() => setParams({})} className="md:hidden text-sm text-[var(--muted)] mb-2">← All messages</button><Chat id={active} onSent={load} /></div> : <p className="hidden md:block text-[var(--muted)] font-serif text-2xl">Pick a conversation</p>}
          </div>
        )}
      </div>
    </div>
  );
}
